import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Award, Heart, Shield, Users, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const About = () => {
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${API}/business-info`);
        setBusinessInfo(response.data);
      } catch (error) {
        console.error('Error fetching business info:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinessInfo();
  }, []);

  const features = [
    {
      icon: Award,
      title: "10+ Years Experience",
      description: "Mastered the art of African braiding with continuous learning and skill development"
    },
    {
      icon: Heart,
      title: "Passionate Care",
      description: "We treat every client's hair with the utmost care and attention to detail"
    },
    {
      icon: Shield,
      title: "Hair Health First",
      description: "Using protective techniques that promote healthy hair growth and retention"
    },
    {
      icon: Users,
      title: "Community Focused",
      description: "Building lasting relationships with our clients and celebrating our culture"
    }
  ];

  if (loading || !businessInfo) {
    return (
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4 flex justify-center items-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-800" />
        </div>
      </section>
    );
  }

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Image Side */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src="https://images.pexels.com/photos/9520394/pexels-photo-9520394.jpeg"
                  alt="Professional braiding"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
                <img
                  src="https://images.pexels.com/photos/11268995/pexels-photo-11268995.jpeg"
                  alt="Cornrow details"
                  className="w-full h-48 object-cover rounded-lg shadow-lg"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src="https://images.unsplash.com/photo-1764265149226-f56ed9b5382f"
                  alt="Box braids with jewelry"
                  className="w-full h-48 object-cover rounded-lg shadow-lg"
                />
                <img
                  src="https://images.pexels.com/photos/7078194/pexels-photo-7078194.jpeg"
                  alt="Beautiful twists"
                  className="w-full h-64 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
            {/* Decorative Element */}
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-amber-800/10 rounded-lg -z-10"></div>
          </div>

          {/* Text Side */}
          <div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              About {businessInfo.name}
            </h2>
            <p className="text-xl text-gray-600 mb-6 leading-relaxed">
              {businessInfo.description}
            </p>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Our home studio in Zollikerberg, Zurich provides a peaceful, clean, and comfortable 
              environment where you can relax while we work our magic on your hair. We believe in 
              celebrating the beauty of natural hair through traditional African braiding techniques 
              passed down through generations.
            </p>
            
            {/* Service Highlights */}
            <div className="bg-amber-50 rounded-lg p-6 mb-8">
              <h3 className="font-bold text-gray-900 mb-3 text-lg">Why Choose Us?</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-amber-800 mr-2">✓</span>
                  <span>Clean, comfortable home studio in Zollikerberg</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-800 mr-2">✓</span>
                  <span>Mobile service available - we can come to you!</span>
                </li>
                <li className="flex items-start">
                  <span className="text-amber-800 mr-2">✓</span>
                  <span>Over 10 years of professional braiding experience</span>
                </li>
              </ul>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <div className="bg-amber-50 px-6 py-3 rounded-lg">
                <div className="text-2xl font-bold text-amber-800">2000+</div>
                <div className="text-sm text-gray-600">Satisfied Clients</div>
              </div>
              <div className="bg-amber-50 px-6 py-3 rounded-lg">
                <div className="text-2xl font-bold text-amber-800">4+ Star</div>
                <div className="text-sm text-gray-600">Average Rating</div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div 
                key={index}
                className="text-center p-6 rounded-lg hover:bg-amber-50 transition-colors duration-300"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-800 text-white rounded-full mb-4">
                  <Icon className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;
